import type { ChartPanel } from '../types'

const COLORS = ['#4f6bed', '#14a38b', '#e98b2a', '#9a5be8', '#e25572', '#3d91c9']
const WIDTH = 920
const HEIGHT = 330
const MARGIN = { top: 26, right: 24, bottom: 68, left: 68 }

type LineChartProps = {
  panel: ChartPanel
  sharedMaximum?: number
}

function compactNumber(value: number) {
  return new Intl.NumberFormat('ko-KR', {
    notation: value >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value)
}

export default function LineChart({ panel, sharedMaximum }: LineChartProps) {
  const plotWidth = WIDTH - MARGIN.left - MARGIN.right
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom
  const localMaximum = Math.max(1, ...panel.series.flatMap((series) => series.values))
  const maximum = Math.max(1, sharedMaximum ?? localMaximum)
  const xStep = plotWidth / Math.max(1, panel.labels.length - 1)
  const ticks = [0, 0.25, 0.5, 0.75, 1]

  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={`${panel.title} 월별 노출수 선 차트`}>
        {ticks.map((ratio) => {
          const y = MARGIN.top + plotHeight - plotHeight * ratio
          return (
            <g key={ratio}>
              <line x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={y} y2={y} className="chart-grid-line" />
              <text x={MARGIN.left - 12} y={y + 4} textAnchor="end" className="chart-axis-text">
                {compactNumber(maximum * ratio)}
              </text>
            </g>
          )
        })}

        {panel.labels.map((label, index) => {
          const x = panel.labels.length === 1 ? MARGIN.left + plotWidth / 2 : MARGIN.left + index * xStep
          return (
            <text
              key={`${label}-${index}`}
              x={x}
              y={HEIGHT - 38}
              textAnchor="end"
              transform={`rotate(-28 ${x} ${HEIGHT - 38})`}
              className="chart-axis-text"
            >
              {label}
            </text>
          )
        })}

        {panel.series.map((series, seriesIndex) => {
          const points = series.values.map((value, index) => {
            const x = panel.labels.length === 1 ? MARGIN.left + plotWidth / 2 : MARGIN.left + index * xStep
            const y = MARGIN.top + plotHeight - (value / maximum) * plotHeight
            return { x, y, value, label: panel.labels[index] }
          })
          const color = COLORS[seriesIndex % COLORS.length]
          return (
            <g key={series.key}>
              {points.length > 1 && (
                <polyline
                  points={points.map((point) => `${point.x},${point.y}`).join(' ')}
                  fill="none"
                  stroke={color}
                  strokeWidth="3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              )}
              {points.map((point, index) => (
                <circle key={index} cx={point.x} cy={point.y} r="4.5" fill="white" stroke={color} strokeWidth="3">
                  <title>{`${point.label} · ${series.label}: ${point.value.toLocaleString('ko-KR')}`}</title>
                </circle>
              ))}
            </g>
          )
        })}
      </svg>

      <div className="chart-legend" aria-label="범례">
        {panel.series.map((series, index) => (
          <span key={series.key}><i style={{ backgroundColor: COLORS[index % COLORS.length] }} />{series.label}</span>
        ))}
      </div>
    </div>
  )
}
